import { printIPTicket } from "./IPPrinter";
import { printCloudTicket } from "./CloudPrinter";
import { printBluetoothTicket } from "./BluetoothPrinter";
import { printUSBTicket } from "./USBPrinter";

// Función para manejar timeouts largos
const safeTimeout = (callback, delay) => {
  const start = performance.now();
  
  return setTimeout(() => {
    callback();
    const end = performance.now();
    if (end - start > 50) { // 50ms threshold
      console.warn(`[Performance] setTimeout handler took ${end - start}ms`);
    }
  }, delay);
};

export const printTicket = async (
  order,
  total,
  appliedPayments,
  property,
  deviceConfig,
  selectedRevenue,
  selectedSpace,
  chequeSeleccionado,
  currentUser,
  devices,
  customParams = {}
) => {
  console.group('[PrinterManager] Iniciando printTicket');
  console.log('[PrinterManager] Parámetros:', {
    property,
    deviceConfig: {
      name: deviceConfig?.name,
      PRNTT: deviceConfig?.PRNTT,
      Rol: deviceConfig?.Rol
    },
    currentUser: currentUser ? { id: currentUser.id, name: currentUser.name } : null,
    chequeSeleccionado,
    orderLength: order?.length,
    total,
    devicesCount: devices?.length,
    isCuentaCliente: customParams?.isCuentaCliente,
    cliente: customParams?.cliente,
    isQR: customParams?.isQR,
    esQR: customParams?.esQR,
    order: JSON.stringify(order, null, 2)
  });

  try {
    // Validación inicial robusta para order
    if (order === null || order === undefined) {
      console.warn('[PrinterManager] Order es null/undefined, usando array vacío');
      order = [];
    }

    // Validar parámetros básicos
    if (!deviceConfig) {
      throw new Error('deviceConfig no proporcionado');
    }
    if (!deviceConfig.PRNTT) {
      throw new Error(`El dispositivo ${deviceConfig.name || 'sin nombre'} no tiene impresora asignada (PRNTT no definido)`);
    }
    if (!devices || !Array.isArray(devices)) {
      throw new Error('Lista de dispositivos no válida o no proporcionada');
    }
    if (customParams.isCuentaCliente && (!customParams.cliente || !chequeSeleccionado)) {
      throw new Error('Faltan datos para impresión por cliente: cliente o cheque no proporcionados');
    }
    
    // Añadir validación para QR printing
    if (customParams.isQR || customParams.esQR) {
      const qrOrder = Array.isArray(order) ? order : (order && order.productos) || [];
      if (!qrOrder || !Array.isArray(qrOrder) || qrOrder.length === 0) {
        console.error('[PrinterManager] Detalles de la validación QR:', {
          isArray: Array.isArray(qrOrder),
          length: qrOrder?.length
        });
        throw new Error('Datos del pedido no válidos para generar QR');
      }
      if (!customParams.cliente || isNaN(parseInt(customParams.cliente)) || parseInt(customParams.cliente) <= 0) {
        console.error('[PrinterManager] Cliente inválido:', { cliente: customParams.cliente });
        throw new Error('El número de cliente no es válido para generar QR');
      }
      // Log qrCode presence for debugging, but don't require it
      console.log('[PrinterManager] QR Data Check:', {
        hasQrCode: qrOrder[0]?.qrCode
      });
    }

    // Buscar la impresora
    console.log(`[PrinterManager] Buscando impresora con name: ${deviceConfig.PRNTT}, Rol: PRNT`);
    const printer = devices.find(
      (device) => device.name === deviceConfig.PRNTT && device.Rol === "PRNT"
    );

    if (!printer) {
      console.error(`[PrinterManager] Dispositivos disponibles:`, devices.map(d => ({
        name: d.name,
        Rol: d.Rol,
        ConfigTipo: d.Config?.[0]?.Tipo
      })));
      throw new Error(`Impresora ${deviceConfig.PRNTT} no encontrada en devices o no es PRNT`);
    }

    console.log('[PrinterManager] Impresora encontrada:', {
      name: printer.name,
      Rol: printer.Rol,
      Config: printer.Config
    });

    // Validar configuración de la impresora
    if (!printer.Config?.[0]?.Tipo) {
      throw new Error(`Impresora ${printer.name} no tiene tipo definido en Config`);
    }

    // Construir printerConfig
    const printerConfig = {
      ...printer.Config.reduce((acc, config) => {
        return { ...acc, [config.Tipo || "IP"]: config[config.Tipo] || config };
      }, {}),
      ...customParams,
      esQR: customParams.esQR || customParams.isQR,
      DeviceID: customParams.DeviceID || printer.name
    };

    // Ajustar DeviceID para USB
    if (printer.Config[0].Tipo.toLowerCase() === "usb" || customParams.esQR || customParams.isQR) {
      printerConfig.USB = {
        ...printerConfig.USB,
        DeviceID: printerConfig.DeviceID || printer.name,
        Tipo: "USB"
      };
    }

    let printResult;
    if (customParams.esQR || customParams.isQR) {
      console.log('[PrinterManager] Forzando impresión QR a USB');
      const adjustedOrder = Array.isArray(order) ? order : (order && order.productos) || order || [];
      printResult = await printUSBTicket(
        adjustedOrder,
        total,
        appliedPayments,
        property,
        deviceConfig,
        selectedRevenue,
        selectedSpace,
        chequeSeleccionado,
        currentUser,
        printerConfig
      );
    } else {
      const printerType = printer.Config[0].Tipo.toLowerCase();
      console.log(`[PrinterManager] Tipo de impresora: ${printerType}`);

      switch (printerType) {
        case "ip":
          printResult = await printIPTicket(
            order, total, appliedPayments, property, deviceConfig,
            selectedRevenue, selectedSpace, chequeSeleccionado, currentUser, printerConfig
          );
          break;
        case "cloud":
          printResult = await printCloudTicket(
            order, total, appliedPayments, property, deviceConfig,
            selectedRevenue, selectedSpace, chequeSeleccionado, currentUser, printerConfig
          );
          break;
        case "bluetooth":
          printResult = await printBluetoothTicket(
            order, total, appliedPayments, property, deviceConfig,
            selectedRevenue, selectedSpace, chequeSeleccionado, currentUser, printerConfig
          );
          break;
        case "usb":
          // Validación segura para order
          let adjustedOrder;
          if (order === null || order === undefined) {
            adjustedOrder = [];
          } else if (Array.isArray(order)) {
            adjustedOrder = order;
          } else if (order && typeof order === 'object' && order.productos) {
            adjustedOrder = order.productos;
          } else {
            adjustedOrder = order;
          }
          
          printResult = await printUSBTicket(
            adjustedOrder, 
            total, 
            appliedPayments, 
            property, 
            deviceConfig,
            selectedRevenue, 
            selectedSpace, 
            chequeSeleccionado, 
            currentUser, 
            printerConfig
          );
          break;
        default:
          throw new Error(`Tipo de impresora no soportado: ${printerType}`);
      }
    }

    console.log('[PrinterManager] Resultado de impresión:', printResult);
    console.groupEnd();
    return printResult;
  } catch (error) {
    console.error('[PrinterManager] Error completo:', {
      message: error.message,
      stack: error.stack,
      context: {
        property,
        deviceConfig,
        printerName: deviceConfig?.PRNTT,
        user: currentUser,
        isCuentaCliente: customParams?.isCuentaCliente,
        cliente: customParams?.cliente,
        isQR: customParams?.isQR,
        esQR: customParams?.esQR,
        order: JSON.stringify(order, null, 2)
      }
    });
    console.groupEnd();
    return { 
      success: false, 
      message: `Error al imprimir${customParams.isCuentaCliente ? ' cuenta de cliente' : (customParams.esQR || customParams.isQR) ? ' código QR' : ''}: ${error.message}`,
      errorDetails: process.env.NODE_ENV === 'development' ? error.stack : null
    };
  }
};

export const printCorteDeCaja = async (
  userData,
  property,
  deviceConfig,
  currentUser,
  devices,
  selectedDate,
  numberFormat,
  currency,
  customParams = {}
) => {
  console.group('[PrinterManager] Iniciando printCorteDeCaja');
  console.log('[PrinterManager] Parámetros:', {
    userData: {
      usuario: userData?.usuario,
      tipo: userData?.tipo,
      totalVentas: userData?.totalVentas,
      cheques: userData?.cheques
    },
    property,
    deviceConfig: {
      name: deviceConfig?.name,
      PRNTT: deviceConfig?.PRNTT,
      Rol: deviceConfig?.Rol
    },
    currentUser: currentUser ? { id: currentUser.id, name: currentUser.name } : null,
    selectedDate,
    numberFormat,
    currency,
    devicesCount: devices?.length,
    customParams
  });

  try {
    if (!deviceConfig) {
      throw new Error('deviceConfig no proporcionado');
    }
    if (!deviceConfig.PRNTT) {
      throw new Error(`El dispositivo ${deviceConfig.name || 'sin nombre'} no tiene impresora asignada (PRNTT no definido)`);
    }
    if (!currentUser) {
      throw new Error('Usuario no proporcionado para corte de caja');
    }
    if (!selectedDate) {
      throw new Error('Fecha de control no proporcionada para corte de caja');
    }
    if (!devices || !Array.isArray(devices)) {
      throw new Error('Lista de dispositivos no válida o no proporcionada');
    }

    console.log(`[PrinterManager] Buscando impresora con name: ${deviceConfig.PRNTT}, Rol: PRNT`);
    const printer = devices.find(
      (device) => device.name === deviceConfig.PRNTT && device.Rol === "PRNT"
    );

    if (!printer) {
      console.error(`[PrinterManager] Dispositivos disponibles:`, devices.map(d => ({
        name: d.name,
        Rol: d.Rol,
        ConfigTipo: d.Config?.[0]?.Tipo
      })));
      throw new Error(`Impresora ${deviceConfig.PRNTT} no encontrada en devices o no es PRNT`);
    }

    console.log('[PrinterManager] Impresora encontrada:', {
      name: printer.name,
      Rol: printer.Rol,
      Config: printer.Config
    });

    if (!printer.Config?.[0]?.Tipo) {
      throw new Error(`Impresora ${printer.name} no tiene tipo definido en Config`);
    }

    const printerConfig = {
      ...printer.Config.reduce((acc, config) => {
        return { ...acc, [config.Tipo || "IP"]: config[config.Tipo] || config };
      }, {}),
      isCorte: true,
      fecha_control: selectedDate,
      corteData: JSON.stringify({
        usuario: userData?.usuario || currentUser?.nombre || 'Desconocido',
        tipo: userData?.tipo,
        totalVentas: userData?.totalVentas,
        totalCobros: userData?.totalCobros,
        cheques: userData?.cheques,
        propinas: userData?.propinas,
        cambio: userData?.cambio,
        enCaja: userData?.enCaja,
        paymentMethods: userData?.paymentDetails,
        fecha_control: selectedDate,
        numberFormat,
        currency
      }),
      ...customParams,
      DeviceID: customParams.DeviceID || printer.name
    };

    if (printer.Config[0].Tipo.toLowerCase() === "usb") {
      printerConfig.USB = {
        ...printerConfig.USB,
        DeviceID: printerConfig.DeviceID || printer.name,
        Tipo: "USB"
      };
    }

    const printerType = printer.Config[0].Tipo.toLowerCase();
    console.log(`[PrinterManager] Tipo de impresora: ${printerType}`);

    let printResult;
    switch (printerType) {
      case "ip":
        printResult = await printIPTicket(
          null,
          userData?.totalVentas || 0,
          [],
          property,
          deviceConfig,
          null,
          null,
          null,
          currentUser,
          printerConfig
        );
        break;
      case "cloud":
        printResult = await printCloudTicket(
          null,
          userData?.totalVentas || 0,
          [],
          property,
          deviceConfig,
          null,
          null,
          null,
          currentUser,
          printerConfig
        );
        break;
      case "bluetooth":
        printResult = await printBluetoothTicket(
          null,
          userData?.totalVentas || 0,
          [],
          property,
          deviceConfig,
          null,
          null,
          null,
          currentUser,
          printerConfig
        );
        break;
      case "usb":
        printResult = await printUSBTicket(
          null,
          userData?.totalVentas || 0,
          [],
          property,
          deviceConfig,
          null,
          null,
          null,
          currentUser,
          printerConfig
        );
        break;
      default:
        throw new Error(`Tipo de impresora no soportado: ${printerType}`);
    }

    console.log('[PrinterManager] Resultado de impresión:', printResult);
    console.groupEnd();
    return {
      success: printResult.success,
      message: printResult.success
        ? `Corte de caja para ${userData?.usuario || currentUser?.nombre || 'Desconocido'} impreso correctamente en ${deviceConfig.PRNTT}.`
        : printResult.message
    };
  } catch (error) {
    console.error('[PrinterManager] Error completo:', {
      message: error.message,
      stack: error.stack,
      context: {
        property,
        deviceConfig,
        printerName: deviceConfig?.PRNTT,
        user: currentUser
      }
    });
    console.groupEnd();
    return { 
      success: false, 
      message: `Error al imprimir corte de caja: ${error.message}`,
      errorDetails: process.env.NODE_ENV === 'development' ? error.stack : null
    };
  }
};